import { useEffect, useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field, DrawerSection } from "@/components/forms/fields";
import { useDuplicateCheck } from "@/hooks/useDuplicateCheck";
import { usersService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { ASSIGNABLE_ROLES, VA_STAFF_ROLES } from "@/lib/constants";
import { roleLabel } from "@/lib/permissions/roles";
import { isOwnerRole } from "@/lib/permissions/permissions";
import { useAuth } from "@/hooks/useAuth";

const EMPTY = { name: "", email: "", role: "WAREHOUSE_STAFF", va_agency_id: "none", warehouse_id: "" };

export function UserDrawer({ open, onOpenChange, record, agencies = [], warehouses = [], onSaved }) {
  const { user: currentUser } = useAuth();
  const isOwner = isOwnerRole(currentUser?.role);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!record;

  useEffect(() => {
    if (open) {
      setForm(
        record
          ? {
              name: record.name || "",
              email: record.email || "",
              role: record.role || "WAREHOUSE_STAFF",
              va_agency_id: record.va_agency_id || "none",
              warehouse_id: record.warehouse_id || "",
            }
          : EMPTY
      );
    }
  }, [open, record]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const isVaRole = VA_STAFF_ROLES.includes(form.role);

  const { result: dup } = useDuplicateCheck(
    () => usersService.checkDuplicate({ email: form.email, exclude_id: record?.id || "" }),
    [form.email, open],
    open && !isEdit && !!form.email.trim()
  );
  const emailConflict = !isEdit && dup?.email_conflict;

  const nameError = !form.name.trim() ? "Name is required" : "";
  const emailError = !isEdit
    ? !form.email.trim()
      ? "Email is required"
      : emailConflict
      ? "A user with this email already exists"
      : ""
    : "";
  const warehouseError = isOwner && !isEdit && !form.warehouse_id ? "Select a warehouse for this user" : "";
  const canSave = !nameError && !emailError && !warehouseError && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const agency = isVaRole && form.va_agency_id !== "none" ? form.va_agency_id : null;
      let res;
      if (isEdit) {
        res = await usersService.update(record.id, {
          name: form.name.trim(),
          role: form.role,
          va_agency_id: agency,
        });
        toast.success("User updated");
      } else {
        res = await usersService.create({
          name: form.name.trim(),
          email: form.email.trim(),
          role: form.role,
          va_agency_id: agency,
          ...(isOwner ? { warehouse_id: form.warehouse_id } : {}),
        });
        toast.success("Invitation created");
      }
      onOpenChange(false);
      onSaved?.(res);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={UserPlus}
      title={isEdit ? "Edit User" : "Invite User"}
      description={isEdit ? "Update this user's role and assignment." : "Create the user and send a secure invitation."}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="user-drawer-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="user-drawer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Send invitation"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <DrawerSection title="User details">
          <Field label="Full Name" required error={nameError}>
            <Input value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="Jordan Lee" data-testid="user-name-input" />
          </Field>
          <Field label="Email" required error={emailError} hint={isEdit ? "Email cannot be changed after creation." : ""}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set("email")(e.target.value)}
              placeholder="jordan@company.com"
              disabled={isEdit}
              data-testid="user-email-input"
            />
          </Field>
        </DrawerSection>

        <DrawerSection title="Role & assignment">
          {isOwner && !isEdit && (
            <Field label="Warehouse" required error={warehouseError}
                   hint="Which warehouse organization does this user belong to?">
              <Select value={form.warehouse_id || undefined} onValueChange={set("warehouse_id")}>
                <SelectTrigger data-testid="user-warehouse-select"><SelectValue placeholder="Select a warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (<SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Role" required>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v, va_agency_id: VA_STAFF_ROLES.includes(v) ? f.va_agency_id : "none" }))}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSIGNABLE_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {isVaRole && (
            <Field label="VA Agency" hint="Which agency does this person belong to?">
              <Select value={form.va_agency_id} onValueChange={set("va_agency_id")}>
                <SelectTrigger data-testid="user-agency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {agencies.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </DrawerSection>
      </div>
    </Drawer>
  );
}
